import React, { FC, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as ExpandMoreIcon } from 'applicaiton/assets/expand-more.svg';
import useOnClickOutside from 'common/hooks/useClickOutside';
import Input from '../Input';
import Checkbox from '../Checkbox';
import { DropdownProps, OptionPackage } from './models';
import {
  DropdownStyled,
  OptionCheckBox,
  OptionItem,
  OptionsList,
} from './styles';

/**
 * DropdownPackage React component that renders a dropdown with options.
 *
 * @param {DropdownProps} props - The properties for the component.
 * @returns {JSX.Element} A DropdownPackage component.
 */
const DropdownPackage: FC<DropdownProps> = ({
  id,
  label,
  placeholder,
  searchPlaceholder,
  value,
  onChange,
  hint,
  disabled,
  options,
  isMulti,
  errorMessage,
  withSearch,
  LeftIcon,
}) => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const [isActive, setIsActive] = useState(false);
  const [searchItem, setSearchItem] = useState('');

  useOnClickOutside(ref, () => setIsActive(false));

  /**
   * useMemo hook to create a memoized version of the optionList array.
   *
   * @returns {OptionPackage[]} An array containing the filtered options.
   */
  const optionList = useMemo(() => {
    if (!searchItem) return options;
    return options.filter((item) =>
      item.label.toLocaleLowerCase().includes(searchItem.toLocaleLowerCase()),
    );
  }, [options, searchItem]);

  /**
   * Function to handle changes in the selected option.
   *
   * @param {string} selectedItem - The value of the selected option.
   * @returns {void} None.
   */
  const handleChange = (selectedItem: string) => {
    if (!Array.isArray(value)) return;
    const valueAlreadyAdded = value.some((item) => item === selectedItem);
    if (valueAlreadyAdded) {
      const filtredValue = value.filter((item) => item !== selectedItem);
      onChange(filtredValue);
    } else {
      onChange([...value, selectedItem]);
    }
    return;
  };

  /**
   * Function to check whether a checkbox is checked for the dropdown multiple.
   *
   * @param {OptionPackage} item - The option package object.
   * @returns {boolean} Whether the checkbox is checked or not.
   */
  const IsValueChecked = (item: OptionPackage) => {
    const isValueArray = Array.isArray(value);
    return isValueArray && value.some((i) => i === item.value);
  };

  /**
   * Function to get the value of the selected option.
   *
   * @returns {string} The label of the selected option or a message indicating the number of selected options.
   */
  const getValue = () => {
    if (isMulti) return `${value.length} ${t('selected')}`;
    const currentOption = optionList.find(
      (item) => String(item.value) === String(value),
    );
    return currentOption?.label;
  };

  /**
   * Function to determine whether the list of options is not empty and whether it's a single or multiple selection dropdown.
   *
   * @returns {boolean} Whether the list of options is not empty and whether it's a single or multiple selection dropdown.
   */
  const isListNotEmpty = isActive && !!options.length;

  /**
   * Function to get the duration text for an option package.
   *
   * @param {OptionPackage} item - The option package object.
   * @returns {JSX.Element} A JSX element containing the duration text.
   */
  const getDurationText = (item: OptionPackage) => {
    const duration = Number(item?.durationInWeeks) || 0;
    const durationNumber = duration > 3 ? duration / 4 : duration;

    const durationName =
      duration > 3
        ? t(
            `professional_profile.subscriptions.${
              duration / 4 === 1 ? 'month' : 'months'
            }`,
          )
        : t(
            `professional_profile.subscriptions.${
              duration === 1 ? 'week' : 'weeks'
            }`,
          );

    return (
      <>
        <h3>{item.label}</h3>
        <p>-</p>
        <span>
          €{item.price} / {durationNumber} {durationName}
        </span>
      </>
    );
  };

  return (
    <DropdownStyled
      isActive={isActive}
      disabled={disabled}
      ref={ref}
      isError={!!errorMessage}
    >
      {label && <label htmlFor={id}>{label}</label>}
      <section onMouseDown={() => !disabled && setIsActive(!isActive)}>
        {LeftIcon ? (
          <div>
            <LeftIcon />
            {!value.length ? <span>{placeholder}</span> : <h2>{getValue()}</h2>}
          </div>
        ) : (
          <>
            {!value.length ? <span>{placeholder}</span> : <h2>{getValue()}</h2>}
          </>
        )}
        <ExpandMoreIcon />
      </section>
      {isListNotEmpty && !isMulti ? (
        <OptionsList className="optionList">
          {withSearch && (
            <Input
              id={'search'}
              type="search"
              value={searchItem}
              onChange={(e) => setSearchItem(e.target?.value || '')}
              placeholder={searchPlaceholder}
              onClear={() => setSearchItem('')}
            />
          )}
          {optionList.map((item) => (
            <OptionItem
              key={item.value}
              onClick={() => {
                onChange(item);
                setIsActive(false);
              }}
              selected={value === String(item.value)}
            >
              {getDurationText(item)}
            </OptionItem>
          ))}
        </OptionsList>
      ) : (
        ''
      )}
      {isListNotEmpty && isMulti ? (
        <OptionsList className="packagesMultiList">
          {withSearch && (
            <Input
              id={'search'}
              type="search"
              value={searchItem}
              onChange={(e) => setSearchItem(e.target?.value || '')}
              placeholder={searchPlaceholder}
              onClear={() => setSearchItem('')}
            />
          )}
          {optionList.map((item, index) => (
            <OptionCheckBox
              key={`${item.value}_${index}`}
              htmlFor={`${item.label}_${index}`}
              selected={IsValueChecked(item)}
            >
              <Checkbox
                id={`${item.label}_${index}`}
                checked={IsValueChecked(item)}
                onChange={() => handleChange(String(item.value))}
              />
              {getDurationText(item)}
            </OptionCheckBox>
          ))}
        </OptionsList>
      ) : (
        ''
      )}
      {(hint || errorMessage) && (
        <span>{errorMessage ? errorMessage : hint}</span>
      )}
    </DropdownStyled>
  );
};

export default DropdownPackage;
